import React from "react";

import { RemoteLogErrorBoundary } from "~/components/common/remote-log-error-boundary";
import { useIntlDocumentTitle } from "~/hooks/use-document-title";
import { useLoadingDelay } from "~/hooks/use-loading-delay";

import { MeetingCard } from "./meeting-card";
import {
  useCancelMeetingMutation,
  useGetDoctorMeetingsQuery,
  useRescheduleMeetingMutation,
} from "./meetings-api";
import { MeetingsTab, useActiveMeetingsTab } from "./meetings-layout";
import { AnimateOpacity, MeetingsEmptyFallback, MeetingsErrorFallback } from "./meetings-shared";
import type { TMeeting } from "./meetings-types";
import { DateString } from "~/types/common";

export function MeetingsEnrolledPage() {
  const activeTab = useActiveMeetingsTab();
  useIntlDocumentTitle(`meetings.enrolled.title.${activeTab}`);
  const meetingsQuery = useGetDoctorMeetingsQuery();

  return (
    <RemoteLogErrorBoundary
      component="meetings_enrolled_page"
      fallbackRender={MeetingsErrorFallback}
      onReset={meetingsQuery.refetch}
    >
      <MeetingsGrid activeTab={activeTab} />
    </RemoteLogErrorBoundary>
  );
}

export type MeetingCardComponentType = React.ComponentType<React.ComponentProps<typeof MeetingCard>>;

/**
 * The upcoming/finished/all meetings grid — data fetch + tab filtering + cancel/reschedule wiring.
 * `activeTab` is a PROP (not read from the URL) so the same grid can back both the legacy enrolled
 * route (`?tab=`) and the redesign-v1 consultations page (local tab state). `card` lets a caller
 * swap the presentation (the redesign passes its own redesign-v1 card) while reusing all the grid's
 * filtering/dim/sort/mutation logic. See
 * app/components/redesign-v1/expert-consultation/expert-consultation-page.tsx.
 */
export function MeetingsGrid({
  activeTab,
  card: MeetingCardComponent = MeetingCard,
  renderEmpty,
  gridClassName = "tw-grid tw-gap-3 desktop-lg:tw-grid-cols-2",
  skeletonCount = 2,
}: {
  activeTab: MeetingsTab;
  card?: MeetingCardComponentType;
  /** Override the empty-state presentation (the redesign passes a redesign-v1 block). */
  renderEmpty?: (props: React.ComponentProps<typeof MeetingsEmptyFallback>) => React.ReactNode;
  gridClassName?: string;
  skeletonCount?: number;
}) {
  const meetingsQuery = useGetDoctorMeetingsQuery();

  const isLoadingMinDuration = useLoadingDelay(meetingsQuery.isLoading);

  const [cancelMeeting] = useCancelMeetingMutation();
  const [rescheduleMeeting] = useRescheduleMeetingMutation();

  if (meetingsQuery.isUninitialized || meetingsQuery.isLoading || isLoadingMinDuration) {
    return (
      <AnimateOpacity className={gridClassName}>
        {Array.from({ length: skeletonCount }, (_, index) => (
          <MeetingCardComponent
            key={index}
            meeting={undefined}
            displayActions={activeTab != MeetingsTab.FINISHED}
            displayJoinLink={activeTab != MeetingsTab.FINISHED}
            onCancel={() => Promise.resolve()}
            onReschedule={() => Promise.resolve()}
          />
        ))}
      </AnimateOpacity>
    );
  }

  if (meetingsQuery.isError) {
    return (
      <MeetingsErrorFallback
        title="meetings.enrolled.error"
        resetErrorBoundary={meetingsQuery.refetch}
      />
    );
  }

  const meetings = filterMeetingsByTab(meetingsQuery.data, activeTab);
  const hasUpcomingMeetings = meetings.some((meeting) => !meeting.is_finished);

  if (meetings.length == 0) {
    const emptyProps: React.ComponentProps<typeof MeetingsEmptyFallback> = {
      title: "meetings.enrolled.none",
      description: `meetings.enrolled.${activeTab}.none`,
      link: hasUpcomingMeetings ? undefined : { to: "/pages/meetings", children: "meetings.tabs.new" },
    };
    return <>{renderEmpty ? renderEmpty(emptyProps) : <MeetingsEmptyFallback {...emptyProps} />}</>;
  }

  function renderMeetings(meetings: TMeeting[]) {
    return (
      <div className={gridClassName}>
        {meetings.map((meeting) => (
          <MeetingCardComponent
            key={meeting.id}
            meeting={meeting}
            dimFinished={activeTab != MeetingsTab.FINISHED && hasUpcomingMeetings}
            displayActions={activeTab != MeetingsTab.FINISHED && !meeting.is_finished}
            onCancel={(cancelReason?: string | null) => {
              cancelMeeting({
                meetingId: meeting.id.toString(),
                cancel_reason: cancelReason,
              }).unwrap();
            }}
            onReschedule={(rescheduleTime: DateString, rescheduleReason?: string) => {
              rescheduleMeeting({
                meetingId: meeting.id.toString(),
                reschedule_reason: rescheduleReason,
                start_time: rescheduleTime,
              }).unwrap();
            }}
          />
        ))}
      </div>
    );
  }

  if (activeTab == MeetingsTab.ALL) {
    const upcomingMeetings = filterMeetingsByTab(meetings, MeetingsTab.UPCOMING);
    const finishedMeetings = filterMeetingsByTab(meetings, MeetingsTab.FINISHED);

    return (
      <div className="tw-flex tw-flex-col tw-gap-6">
        {upcomingMeetings.length > 0 ? renderMeetings(upcomingMeetings) : null}
        {finishedMeetings.length > 0 ? renderMeetings(finishedMeetings) : null}
      </div>
    );
  }

  return renderMeetings(meetings);
}

export function filterMeetingsByTab(meetings: TMeeting[], activeTab: MeetingsTab) {
  switch (activeTab) {
    case MeetingsTab.UPCOMING:
      return meetings.filter((meeting) => !meeting.is_finished);

    case MeetingsTab.FINISHED:
      return meetings
        .filter((meeting) => meeting.is_finished)
        .sort(function byStartTimeDesc(a, b) {
          return new Date(b.start_time_utc).getTime() - new Date(a.start_time_utc).getTime();
        });

    case MeetingsTab.ALL:
      return meetings;
  }
}
